import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { ArrowLeft, CalendarCheck, CheckCircle2, Loader2 } from "lucide-react";

export default function RdvPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [jours, setJours] = useState([]);
  const [jour, setJour] = useState("");
  const [heure, setHeure] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    api
      .get(`/estimations/${id}`)
      .then((r) => {
        setDoc(r.data);
        if (r.data.rdv) setBooked(r.data.rdv);
      })
      .catch(() => setError("Cette estimation est introuvable."));
    api
      .get("/rdv/disponibilites")
      .then((r) => {
        setJours(r.data.jours);
        const first = r.data.jours.find((j) => j.heures.length > 0);
        if (first) setJour(first.date);
      })
      .catch(() => toast.error("Impossible de charger les disponibilités."));
  }, [id]);

  const heuresJour = useMemo(() => jours.find((j) => j.date === jour)?.heures || [], [jours, jour]);

  const fmtJour = (iso) =>
    new Intl.DateTimeFormat("fr-FR", { weekday: "short", day: "numeric", month: "short" }).format(
      new Date(`${iso}T12:00:00`)
    );

  const submit = async () => {
    if (!jour || !heure) return toast.error("Choisissez un jour et un horaire.");
    setSubmitting(true);
    try {
      const res = await api.post(`/estimations/${id}/rdv`, { date: jour, heure, message: message.trim() || null });
      setBooked(res.data.rdv);
      toast.success("Rendez-vous confirmé — invitation envoyée par email.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossible de réserver ce créneau.");
      if (err.response?.status === 409) {
        api.get("/rdv/disponibilites").then((r) => setJours(r.data.jours)).catch(() => {});
      }
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <header className="border-b border-[#D4AF37]/10 bg-[#0B0B0D]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
        <Link to="/" data-testid="rdv-brand-link" className="font-display text-xl text-[#F5F3EF]">
          Valeur <span className="italic text-[#D4AF37]">Prestige</span>
        </Link>
        <Link to={`/rapport/${id}`} data-testid="rdv-back-link" className="btn-ghost !px-4 !py-2 !text-xs">
          <ArrowLeft size={14} /> Retour au rapport
        </Link>
      </div>
    </header>
  );

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] text-[#F5F3EF]">
        {header}
        <div className="flex flex-col items-center px-6 py-32 text-center">
          <p data-testid="rdv-error" className="font-display text-2xl text-amber-100">{error}</p>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0B0D]">
        <Loader2 data-testid="rdv-loading" className="animate-spin text-[#D4AF37]" size={28} />
      </div>
    );
  }

  return (
    <div data-testid="rdv-page" className="min-h-screen bg-[#0B0B0D] text-[#F5F3EF]">
      {header}
      <main className="mx-auto max-w-4xl px-6 py-14">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80">Visite confidentielle</p>
        <h1 className="font-display mt-4 text-3xl font-light text-amber-100 sm:text-4xl">
          Réservez votre <em className="text-[#D4AF37]">rendez-vous.</em>
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-stone-400">
          Une heure avec votre conseiller dédié, chez vous, pour affiner l'estimation de votre {doc.property.type_bien} au{" "}
          {doc.property.rue}, {doc.property.code_postal} {doc.property.ville} — sans aucun engagement.
        </p>

        {booked ? (
          <div data-testid="rdv-booked" className="mt-12 border border-emerald-500/30 bg-emerald-500/5 p-10 text-center">
            <CheckCircle2 size={28} className="mx-auto text-emerald-400" />
            <p className="font-display mt-4 text-2xl text-emerald-300">Rendez-vous confirmé</p>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-stone-400">
              <span className="capitalize text-stone-200">{fmtJour(booked.date)}</span>{" "}
              <span className="text-stone-200">à {booked.heure}</span> — une confirmation et une invitation
              calendrier vous ont été envoyées par email. Votre conseiller arrive avec les comparables les plus récents de votre rue.
            </p>
            {booked.id && (
              <a
                href={`${API}/rdv/${booked.id}/ics`}
                data-testid="rdv-ics-download"
                className="btn-ghost mt-8 !text-xs"
              >
                <CalendarCheck size={14} /> Ajouter à mon calendrier (.ics)
              </a>
            )}
          </div>
        ) : (
          <div className="mt-12 space-y-10">
            <div>
              <label className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">Le jour</label>
              <div className="flex flex-wrap gap-2" data-testid="rdv-jours">
                {jours.map((j) => (
                  <button
                    key={j.date}
                    type="button"
                    data-testid={`rdv-jour-${j.date}`}
                    onClick={() => {
                      setJour(j.date);
                      setHeure("");
                    }}
                    disabled={j.heures.length === 0}
                    className={`border px-4 py-3 text-sm capitalize transition-[border-color,background-color] duration-300 disabled:opacity-30 ${
                      jour === j.date
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-amber-100"
                        : "border-stone-700 bg-[#141417] text-stone-300 hover:border-[#D4AF37]/50"
                    }`}
                  >
                    {fmtJour(j.date)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">L'horaire</label>
              {heuresJour.length === 0 ? (
                <p className="text-sm text-stone-500" data-testid="rdv-no-slot">Plus de créneau ce jour-là — choisissez une autre date.</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="rdv-heures">
                  {heuresJour.map((h) => (
                    <button
                      key={h}
                      type="button"
                      data-testid={`rdv-heure-${h.replace(":", "h")}`}
                      onClick={() => setHeure(h)}
                      className={`border px-4 py-3 font-mono text-sm transition-[border-color,background-color] duration-300 ${
                        heure === h
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-amber-100"
                          : "border-stone-700 bg-[#141417] text-stone-300 hover:border-[#D4AF37]/50"
                      }`}
                    >
                      {h}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">
                Un mot pour votre conseiller (optionnel)
              </label>
              <textarea
                data-testid="rdv-message"
                rows={3}
                maxLength={500}
                className="input-dark resize-none"
                placeholder="Accès, digicode, point d'attention particulier sur le bien…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <button
              type="button"
              data-testid="rdv-submit-button"
              onClick={submit}
              disabled={submitting || !jour || !heure}
              className="btn-gold disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Réservation…
                </>
              ) : (
                <>
                  <CalendarCheck size={16} /> Confirmer le rendez-vous
                </>
              )}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
